import { FC, useMemo, useState } from 'react'
import { useExecutePreviewJs } from '@mobileHooks'
import { FormElement, MobileForm } from '@mobileTypes'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PhoneIcon from '@mui/icons-material/Phone'
import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material'

import { EmbeddedListElementPreview } from './components/EmbeddedListElementPreview'
import { ListItemElementPreview } from './components/ListItemElementPreview'
import { MobilePhoneFrame } from './frames/MobilePhoneFrame'

interface MobileFormPreviewProps {
  form: Partial<MobileForm>
}

interface PreviewRenderContext {
  swipeDisabled: boolean
  hasBottomContent: boolean
  resolveTitle: (element: FormElement) => string
}

// Mock command results for preview
const MOCK_COMMAND_RESULTS: Record<string, any[]> = {
  command1: [
    { _id: '1', description: 'Прием багажа, не пригодного для воздушной перевозки' },
    {
      _id: '2',
      description: 'Не обращено внимание пассажира на необходимость изъятия опасных вещей',
    },
    { _id: '3', description: 'Не внесена информация о весе, количестве мест багажа в систему DCS' },
  ],
}

const noopCheckboxChange: (itemId: string, checked: boolean) => void = () => undefined
const noopContextMenuAction: (action: unknown, item: unknown) => void = () => undefined

const FIELD_LABEL_SX = {
  width: 120,
  flexShrink: 0,
  py: 1,
  px: 1.25,
  backgroundColor: '#F3F3F3',
  borderRadius: '8px',
  border: '1px solid #E2E2E2',
} as const

const FIELD_VALUE_SX = {
  flex: 1,
  minWidth: 0,
  py: 1,
  px: 1.25,
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  border: '1px solid #E2E2E2',
} as const

const hasDisabledSwipe = (elements: FormElement[] | undefined): boolean =>
  (elements || []).some(
    element =>
      (element.type === 'SWIPE' && Boolean(element.disabledIfJs?.trim())) ||
      hasDisabledSwipe(element.accordionChildren)
  )

interface AccordionPreviewProps {
  element: FormElement
  selectedIds: string[]
  renderContext: PreviewRenderContext
  onCheckboxChange?: (itemId: string, checked: boolean) => void
  onContextMenuAction?: (action: any, item: any) => void
}

const AccordionPreview: FC<AccordionPreviewProps> = ({
  element,
  selectedIds,
  renderContext,
  onCheckboxChange,
  onContextMenuAction,
}) => {
  const [expanded, setExpanded] = useState(false)
  const children = element.accordionChildren || []
  const title = element.title || ''

  return (
    <Box sx={{ width: '100%', mb: 0.5 }}>
      <Box
        onClick={() => setExpanded(prev => !prev)}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          height: '50px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Typography
          sx={{
            flex: 1,
            fontWeight: 600,
            fontSize: '14px',
            color: '#000',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
        <ExpandMoreIcon
          sx={{
            color: '#666',
            fontSize: 20,
            transition: 'transform 0.2s ease',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Box>
      {expanded && (
        <Box sx={{ pt: 0.5, pb: 1 }}>
          {children.length === 0 ? (
            <Typography
              sx={{
                fontSize: '13px',
                color: '#ACACAC',
                textAlign: 'center',
                py: 1.5,
              }}
            >
              No elements
            </Typography>
          ) : (
            children.map((child, childIdx) =>
              renderFormElement(
                child,
                childIdx,
                false,
                selectedIds,
                renderContext,
                onCheckboxChange,
                onContextMenuAction
              )
            )
          )}
        </Box>
      )}
    </Box>
  )
}

const renderFormElement = (
  element: FormElement,
  index: number,
  isBottomElement = false,
  selectedIds: string[] = [],
  renderContext: PreviewRenderContext,
  onCheckboxChange?: (itemId: string, checked: boolean) => void,
  onContextMenuAction?: (action: any, item: any) => void
) => {
  const { type, title, data, editable = true } = element

  switch (type) {
    case 'FIELD': {
      if (!editable) {
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: data?.isMultiline ? 'flex-start' : 'center',
              mb: 1,
              gap: 1,
            }}
          >
            <Box sx={FIELD_LABEL_SX}>
              <Typography
                sx={{
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {data?.title || title || 'Label'}
              </Typography>
            </Box>
            <Box sx={FIELD_VALUE_SX}>
              <Typography sx={{ fontSize: '14px', color: '#333' }}>Value</Typography>
            </Box>
          </Box>
        )
      }

      const fieldLabel = data?.title || title || 'Field'
      const isRequired = data?.userRequired || data?.defaultRequired
      const displayLabel = isRequired ? `${fieldLabel}*` : fieldLabel
      const placeholderText = data?.placeholderValue || `Введите ${fieldLabel.toLowerCase()}`

      if (data?.type === 'enum') {
        return (
          <Box key={index} sx={{ mb: 1.5 }}>
            <Box
              sx={{
                minHeight: 54,
                borderRadius: '8px',
                border: '1px solid #E0E0E0',
                backgroundColor: '#fff',
                px: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '10px', color: '#9E9E9E', lineHeight: 1.2 }}>
                  {displayLabel}
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#ACACAC' }} noWrap>
                  {placeholderText}
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ color: '#333', fontSize: 28 }} />
            </Box>
          </Box>
        )
      }

      return (
        <Box key={index} sx={{ mb: 1.5 }}>
          <TextField
            fullWidth
            label={displayLabel}
            placeholder={placeholderText}
            multiline={data?.isMultiline}
            minRows={data?.isMultiline ? 3 : undefined}
            variant='outlined'
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                minHeight: data?.isMultiline ? undefined : 54,
                fontSize: '14px',
                backgroundColor: '#fff',
              },
              '& .MuiInputLabel-root': {
                fontSize: '14px',
                color: '#9E9E9E',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E0E0E0',
              },
            }}
          />
        </Box>
      )
    }

    case 'PHONE_NUMBER': {
      const label = data?.title || title || 'Телефон'

      return (
        <Box
          key={index}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            mb: 1,
            gap: 1,
          }}
        >
          <Box sx={FIELD_LABEL_SX}>
            <Typography
              sx={{
                fontSize: '14px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {label}
            </Typography>
          </Box>
          <Box
            sx={{
              ...FIELD_VALUE_SX,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <PhoneIcon sx={{ color: '#0066CC', fontSize: 20 }} />
            <Typography sx={{ fontSize: '14px', color: '#0066CC' }} noWrap>
              +7 900 000-00-00
            </Typography>
          </Box>
        </Box>
      )
    }

    case 'TITLE':
      return (
        <Box key={index} sx={{ my: 0.75 }}>
          <Typography
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              fontSize: '16px',
              color: '#000',
            }}
          >
            {title || 'Title'}
          </Typography>
        </Box>
      )

    case 'DIVIDER':
      return <Box key={index} sx={{ height: 8 }} />

    case 'BUTTON': {
      const buttonBackground = element.buttonColor || '#06c'
      const buttonTitle =
        renderContext.resolveTitle(element) || title || element.command?.name || 'Button'

      return (
        <Box key={index} sx={{ width: '100%', mt: isBottomElement ? 0 : 0.75 }}>
          <Button
            fullWidth
            variant='contained'
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '18px',
              textTransform: 'none',
              borderRadius: '10px',
              backgroundColor: buttonBackground,
              minHeight: 40,
              py: 1,
              px: 1.5,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: buttonBackground,
                opacity: 0.85,
                boxShadow: 'none',
              },
            }}
          >
            {buttonTitle}
          </Button>
        </Box>
      )
    }

    case 'SWIPE': {
      const swipeTitle = renderContext.resolveTitle(element) || title || 'Swipe'
      const swipeBlocked = renderContext.swipeDisabled && Boolean(element.disabledIfJs?.trim())
      const trackColor = swipeBlocked
        ? '#E0E0E0'
        : renderContext.hasBottomContent
        ? '#E8F5E9'
        : '#F5F5F5'

      return (
        <Box key={index} sx={{ width: '100%', my: 2 }}>
          <Box
            sx={{
              position: 'relative',
              height: '50px',
              width: '100%',
              backgroundColor: trackColor,
              borderRadius: '25px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              opacity: swipeBlocked ? 0.6 : 1,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: '10px',
                height: '30px',
                width: '30px',
                borderRadius: '50%',
                backgroundColor: '#06d6a0',
                zIndex: 3,
              }}
            />
            <Typography
              sx={{
                color: '#1b9aaa',
                zIndex: 2,
                fontSize: '16px',
                fontWeight: 700,
                opacity: 0.7,
              }}
            >
              {swipeTitle}
            </Typography>
          </Box>
        </Box>
      )
    }

    case 'FILE': {
      const isImage = (element.fileTypes || []).includes('image')
      const isEditable = element.editable !== false
      const pickLabel = isImage ? 'Добавить фото' : 'Добавить файл'

      return (
        <Box key={index} sx={{ my: 0.75 }}>
          {title ? (
            <Typography sx={{ fontSize: '13px', color: '#666', mb: 0.75 }}>{title}</Typography>
          ) : null}
          <Box
            sx={{
              width: '100%',
              height: 120,
              borderRadius: '8px',
              backgroundColor: '#f5f5f5',
              border: '1px dashed #ddd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ color: '#aaa', fontSize: '14px' }}>
              {isImage ? 'Нет фото' : 'Нет файла'}
            </Typography>
          </Box>
          {isEditable && (
            <Box
              sx={{
                mt: 1,
                py: 1.25,
                px: 2,
                borderRadius: '6px',
                border: '1px solid #508d6b',
                textAlign: 'center',
              }}
            >
              <Typography sx={{ color: '#508d6b', fontWeight: 600, fontSize: '14px' }}>
                {pickLabel}
              </Typography>
            </Box>
          )}
        </Box>
      )
    }

    case 'EMBEDDED_LIST':
      return (
        <Box key={index} sx={{ width: '100%', mb: 2 }}>
          {element.embeddedListItemId || element.embeddedListConfig ? (
            <EmbeddedListElementPreview
              element={element}
              commandResults={MOCK_COMMAND_RESULTS}
              selectedIds={selectedIds}
              onCheckboxChange={onCheckboxChange || noopCheckboxChange}
              onContextMenuAction={onContextMenuAction || noopContextMenuAction}
            />
          ) : (
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant='body2' color='text.secondary'>
                Configure embedded list
              </Typography>
            </Box>
          )}
        </Box>
      )

    case 'LIST_ITEM':
      return (
        <Box key={index} sx={{ width: '100%', mb: 2 }}>
          {element.listItemConfigId ? (
            <ListItemElementPreview element={element} />
          ) : (
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant='body2' color='text.secondary'>
                Configure list item
              </Typography>
            </Box>
          )}
        </Box>
      )

    case 'ACCORDION':
      return (
        <AccordionPreview
          key={index}
          element={element}
          selectedIds={selectedIds}
          renderContext={renderContext}
          onCheckboxChange={onCheckboxChange}
          onContextMenuAction={onContextMenuAction}
        />
      )

    default:
      return null
  }
}

export const MobileFormPreview: FC<MobileFormPreviewProps> = ({ form }) => {
  const elements = form.elements || []
  const bottomElements = form.bottomElements || []
  const hasBottomElements = bottomElements.length > 0
  const executePreviewJs = useExecutePreviewJs()
  const [swipeDisabled, setSwipeDisabled] = useState(false)

  const showSwipeDisabledToggle = useMemo(
    () => hasDisabledSwipe(form.elements) || hasDisabledSwipe(form.bottomElements),
    [form.elements, form.bottomElements]
  )

  const renderContext = useMemo<PreviewRenderContext>(() => {
    const resolveTitle = (element: FormElement) => {
      const script = element.titleJs?.trim()
      if (!script) {
        return element.title || ''
      }

      const { value, error } = executePreviewJs(script, {
        item: {},
        formData: {},
        localVariables: {},
        globalVariables: {},
      })

      if (error || value == null || value === '') {
        return element.title || ''
      }

      return String(value)
    }

    return {
      swipeDisabled,
      hasBottomContent: hasBottomElements,
      resolveTitle,
    }
  }, [executePreviewJs, hasBottomElements, swipeDisabled])

  const [selectedIdsMap, setSelectedIdsMap] = useState<Record<string, string[]>>({})

  const handleCheckboxChange = (elementIndex: number, itemId: string, checked: boolean) => {
    const element = elements[elementIndex]
    if (element?.type !== 'EMBEDDED_LIST') return

    const variableName = element.selectedIdsVariable
    if (!variableName) return

    setSelectedIdsMap(prev => {
      const currentIds = prev[variableName] || []
      const newIds = checked ? [...currentIds, itemId] : currentIds.filter(id => id !== itemId)

      return {
        ...prev,
        [variableName]: newIds,
      }
    })
  }

  const handleContextMenuAction = () => undefined

  const getSelectedIdsForElement = (element: FormElement): string[] => {
    if (element.type !== 'EMBEDDED_LIST' || !element.selectedIdsVariable) {
      return []
    }

    return selectedIdsMap[element.selectedIdsVariable] || []
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      {showSwipeDisabledToggle && (
        <FormControlLabel
          control={
            <Checkbox
              size='small'
              checked={swipeDisabled}
              onChange={event => setSwipeDisabled(event.target.checked)}
            />
          }
          label='Свайп заблокирован (disabledIfJs)'
        />
      )}
      <MobilePhoneFrame title='📱 Form Preview'>
        <Box
          sx={{
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#fff',
          }}
        >
          <Box
            sx={{
              height: '100%',
              overflow: 'auto',
              scrollbarWidth: 'thin',
              backgroundColor: '#fff',
              pb: hasBottomElements ? '96px' : '16px',
            }}
          >
            <Box
              sx={{
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                px: 2,
                py: 2,
              }}
            >
              {elements.map((element, index) =>
                renderFormElement(
                  element,
                  index,
                  false,
                  getSelectedIdsForElement(element),
                  renderContext,
                  (itemId: string, checked: boolean) =>
                    handleCheckboxChange(index, itemId, checked),
                  handleContextMenuAction
                )
              )}
            </Box>
          </Box>

          {hasBottomElements && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                backgroundColor: '#fff',
                borderTop: '1px solid #E2E2E2',
                boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
              }}
            >
              {bottomElements.map((element, index) => (
                <Box
                  key={element.internalId || index}
                  sx={{
                    flex: element.size ?? 12,
                    minWidth: 0,
                  }}
                >
                  {renderFormElement(element, index, true, [], renderContext)}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </MobilePhoneFrame>
    </Box>
  )
}
